import React from "react";

/**
 * Decorative SVG elements scattered around pages.
 * Stars, balloons, simple toys. Subtle (low opacity), floating gently.
 * Use absolute positioning from parent.
 */

export const Star = ({ className = "", color = "#FBBF24", size = 32 }) => (
  <svg
    aria-hidden="true"
    className={className}
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill={color}
  >
    <path d="M12 1.5l3.09 6.26L22 8.77l-5 4.87 1.18 6.88L12 17.27l-6.18 3.25L7 13.64l-5-4.87 6.91-1.01L12 1.5z" />
  </svg>
);

export const Balloon = ({ className = "", color = "#9333EA", size = 36 }) => (
  <svg
    aria-hidden="true"
    className={className}
    width={size}
    height={size}
    viewBox="0 0 40 56"
    fill="none"
  >
    <ellipse cx="20" cy="20" rx="14" ry="18" fill={color} />
    <path d="M20 38 L18 42 L22 42 Z" fill={color} />
    <path
      d="M20 42 Q22 48 18 50 Q22 52 20 56"
      stroke="#4B5563"
      strokeWidth="1.2"
      fill="none"
      strokeLinecap="round"
    />
    <ellipse cx="15" cy="14" rx="3" ry="5" fill="#fff" opacity="0.4" />
  </svg>
);

export const ToyBlock = ({ className = "", color = "#16A34A", size = 36 }) => (
  <svg
    aria-hidden="true"
    className={className}
    width={size}
    height={size}
    viewBox="0 0 40 40"
    fill="none"
  >
    <rect x="3" y="3" width="34" height="34" rx="6" fill={color} />
    <circle cx="20" cy="20" r="6" fill="#FFFCF9" />
    <circle cx="20" cy="20" r="2.5" fill={color} />
  </svg>
);

export const Crayon = ({ className = "", color = "#EF4444", size = 40 }) => (
  <svg
    aria-hidden="true"
    className={className}
    width={size}
    height={size}
    viewBox="0 0 16 48"
    fill="none"
  >
    <rect x="2" y="10" width="12" height="32" rx="2" fill={color} />
    <rect x="2" y="14" width="12" height="2" fill="#fff" opacity="0.6" />
    <rect x="2" y="38" width="12" height="2" fill="#fff" opacity="0.6" />
    <path d="M2 10 L8 0 L14 10 Z" fill={color} />
    <path d="M2 42 L8 48 L14 42 Z" fill="#1F2937" />
  </svg>
);

export const CloudShape = ({ className = "", color = "#F3E8FF", size = 80 }) => (
  <svg
    aria-hidden="true"
    className={className}
    width={size}
    height={size * 0.6}
    viewBox="0 0 100 60"
    fill={color}
  >
    <path d="M20 50 Q5 50 5 35 Q5 22 18 22 Q20 8 35 8 Q50 8 52 22 Q70 18 75 32 Q90 32 90 45 Q90 55 78 55 H22 Q20 55 20 50 Z" />
  </svg>
);

/** Container that scatters decorations absolutely behind content */
export function DecorationField({ variant = "mixed" }) {
  // variant-driven positions
  const items = {
    mixed: [
      { c: <Star color="#FBBF24" size={26} />, pos: "top-12 left-[6%]", anim: "animate-float" },
      { c: <Balloon color="#9333EA" size={40} />, pos: "top-24 right-[8%]", anim: "animate-float-slow" },
      { c: <ToyBlock color="#16A34A" size={32} />, pos: "top-[55%] left-[4%]", anim: "animate-float" },
      { c: <Star color="#9333EA" size={20} />, pos: "top-[40%] right-[5%]", anim: "animate-float-slow" },
      { c: <Crayon color="#EF4444" size={42} />, pos: "bottom-24 right-[12%] rotate-12", anim: "animate-wiggle" },
      { c: <Star color="#16A34A" size={22} />, pos: "bottom-32 left-[10%]", anim: "animate-float" },
    ],
    hero: [
      { c: <Star color="#FBBF24" size={28} />, pos: "top-10 left-8", anim: "animate-float" },
      { c: <Balloon color="#9333EA" size={44} />, pos: "top-6 right-10", anim: "animate-float-slow" },
      { c: <ToyBlock color="#16A34A" size={36} />, pos: "bottom-20 left-[4%]", anim: "animate-float" },
      { c: <Crayon color="#FBBF24" size={40} />, pos: "bottom-16 right-[6%] -rotate-12", anim: "animate-wiggle" },
    ],
  };
  const set = items[variant] || items.mixed;
  return (
    <div
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 overflow-hidden"
      data-testid="decoration-field"
    >
      {set.map((d, i) => (
        <div key={i} className={`absolute opacity-70 ${d.pos} ${d.anim}`}>
          {d.c}
        </div>
      ))}
    </div>
  );
}
